import React, { useState, useEffect } from "react";
import {
  Users,
  ShoppingBag,
  MessageSquare,
  Image as ImageIcon,
  TrendingUp,
  Activity,
  ArrowUpRight,
  UserCheck,
  UserX,
  Link
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";

const API_URL = (import.meta.env.VITE_API_URL || "http://localhost:3001") + "/api/admin";

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    mediaCount: 0,
    newsCount: 0,
    supportCount: 0,
    storeCount: 0,
    totalUsers: 0,
    activeUsers: 0,
    suspendedUsers: 0,
  });
  const token = localStorage.getItem("ka_admin_token");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [media, news, support, store, userStats] = await Promise.all([
          fetch(`${API_URL}/media`).then(r => r.json()),
          fetch(`${API_URL}/news`).then(r => r.json()),
          fetch(`${API_URL}/support`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
          fetch(`${API_URL}/store`).then(r => r.json()),
          fetch(`${API_URL}/users/stats`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        ]);

        setStats({
          mediaCount: Array.isArray(media) ? media.length : 0,
          newsCount: Array.isArray(news) ? news.length : 0,
          supportCount: Array.isArray(support) ? support.length : 0,
          storeCount: Array.isArray(store) ? store.length : 0,
          totalUsers: userStats?.total || 0,
          activeUsers: userStats?.active || 0,
          suspendedUsers: userStats?.suspended || 0,
        });
      } catch (err) {
        console.error("Failed to fetch dashboard stats", err);
      }
    };

    fetchStats();
  }, [token]);

  const statItems = [
    { name: "Registered Users", value: stats.totalUsers, icon: Users, color: "text-blue-400", path: "/admin/users" },
    { name: "Active Users", value: stats.activeUsers, icon: UserCheck, color: "text-green-400", path: "/admin/users" },
    { name: "Support Tickets", value: stats.supportCount, icon: MessageSquare, color: "text-orange-400", path: "/admin/support" },
    { name: "Store Items", value: stats.storeCount, icon: ShoppingBag, color: "text-purple-400", path: "/admin/store" },
  ];

  return (
    <div className="space-y-8">
      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statItems.map((item) => (
          <Card
            key={item.name}
            onClick={() => navigate(item.path)}
            className="bg-[#1a1a1a]/80 border-[#8B0000]/30 overflow-hidden relative group cursor-pointer hover:border-[#8B0000]/60 transition-colors"
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-white uppercase tracking-wider">{item.name}</CardTitle>
              <item.icon className={`w-5 h-5 ${item.color}`} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{item.value}</div>
              <p className="text-xs text-white/60 mt-1 flex items-center">
                <ArrowUpRight className="w-3 h-3 mr-1 text-green-500" />
                Updated just now
              </p>
            </CardContent>
            <div className="absolute bottom-0 left-0 w-full h-1 bg-[#8B0000]/20 group-hover:bg-[#8B0000]/60 transition-all duration-300" />
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Media & News */}
        <Card className="bg-[#1a1a1a]/80 border-[#8B0000]/30 h-[360px]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Activity className="w-5 h-5 text-[#8B0000]" />
              Content Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { label: "Media items uploaded", value: stats.mediaCount, icon: ImageIcon, color: "text-purple-400" },
              { label: "News & updates", value: stats.newsCount, icon: TrendingUp, color: "text-blue-400" },
              { label: "Suspended accounts", value: stats.suspendedUsers, icon: UserX, color: "text-red-400" },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03] border border-[#8B0000]/10">
                <div className="flex items-center gap-3">
                  <item.icon className={`w-4 h-4 ${item.color}`} />
                  <span className="text-sm text-gray-300">{item.label}</span>
                </div>
                <span className="text-lg font-bold text-white">{item.value}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Quick links */}
        <Card className="bg-[#1a1a1a]/80 border-[#8B0000]/30 h-[360px]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Users className="w-5 h-5 text-[#8B0000]" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { label: "Manage Users", desc: "View, activate or suspend accounts", path: "/admin/users", color: "text-blue-400" },
              { label: "Edit Page Content", desc: "Update homepage and other pages", path: "/admin/content", color: "text-green-400" },
              { label: "Support Messages", desc: "View and respond to player tickets", path: "/admin/support", color: "text-orange-400" },
              { label: "Media Gallery", desc: "Upload and manage site images", path: "/admin/media", color: "text-purple-400" },
            ].map((item) => (
              <div
                key={item.label}
                onClick={() => navigate(item.path)}
                className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03] border border-[#8B0000]/10 cursor-pointer hover:bg-white/[0.06] hover:border-[#8B0000]/30 transition-all group"
              >
                <div>
                  <p className={`text-sm font-medium ${item.color}`}>{item.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-gray-600 group-hover:text-white transition-colors" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
